import NilUUID from '../NilUUID'
import _ from 'lodash'
import jxon from '@infomaker/im-jxon'

/**
 * @description
 * Common trait used for Nodes which needs functions used in author concept handling.
 * ### Usage
 * Extending class's schema requires the following property definitions in its `schema`:
 * ```
 * authors: {type: 'array', default: []}
 * ```
 *
 * @memberof Trait
 * @typedef Trait.authorNodeTrait
 * @mixin authorNodeTrait
 *
 * @example
 * import {withTraits, traitBundle} from 'writer'
 * import {BlockNode} from 'substance'
 * const {authorNodeTrait} = traitBundle
 *
 * class MyAuthorNode extends withTraits(BlockNode, authorNodeTrait) {
 *    // Class MyAuthorNode will contain all functions from authorNodeTrait
 * }
 *
 * MyAuthorNode.define({
 *    type: 'myauthor',
 *    authors: {type: 'array', default: []}
 * })
 *
 * @property {function} authorNodeTrait.addAuthor
 * @property {function} authorNodeTrait.setAuthors
 * @property {function} authorNodeTrait.removeAuthor
 * @property {function} authorNodeTrait.fetchAuthorConcept
 * @property {function} authorNodeTrait.findAttribute
 * @property {function} authorNodeTrait.fetchAuthorsConcept
 * @property {function} authorNodeTrait.updateAuthorFromConcept
 */
const authorNodeTrait = (api) => {
    return {
        /**
         * @memberOf Trait.authorNodeTrait
         * @param author
         */
        addAuthor(author) {
            const authors = this.authors
            let result = authors.find((item) => this._isEqual(item, author))

            if (result !== undefined) {
                // Do not add author is it already exist
                throw new Error('Cannot add selected author to list of authors due to: Author already exists in list')
            }

            authors.push(author)

            // First add the authors to the node
            api.editorSession.transaction((tx) => {
                tx.set([this.id, 'authors'], authors)
            })

            /*
                We the author is added we try to load the concept newsitem for this author.
                When concepts information is fetch we update the authors on the node yet again.
                Only fetch information for authors that has a uuid
             */
            if (!author.isSimpleAuthor) {
                this.updateAuthorFromConcept(author)
            }
        },

        /**
         * @memberOf Trait.authorNodeTrait
         * @param authors
         */
        setAuthors(authors) {
            api.editorSession.transaction((tx) => {
                tx.set([this.id, 'authors'], authors)
            })
        },

        /**
         * @memberOf Trait.authorNodeTrait
         * @param author
         */
        removeAuthor(author) {
            const authors = this.authors
            for(let n = 0; n < authors.length; n++) {

                if (!NilUUID.isNilUUID(author.uuid) && authors[n].uuid === author.uuid) {
                    authors.splice(n, 1)
                    break
                }
                else if (NilUUID.isNilUUID(author.uuid) && authors[n].name === author.name) {
                    authors.splice(n, 1)
                    break
                }
            }
            // First add the authors to the node
            api.editorSession.transaction((tx) => {
                tx.set([this.id, 'authors'], authors)
            })
        },

        /**
         * @memberOf Trait.authorNodeTrait
         * @param author
         * @returns {Promise}
         */
        fetchAuthorConcept(author) {

            if (author.isSimpleAuthor) {
                return Promise.resolve(author)
            }

            return new Promise((resolve, reject) => {
                api.router.getConceptItem(author.uuid, 'x-im/author')
                    .then((dom) => {
                        const conceptXML = dom.querySelector('concept')
                        const linksXML = dom.querySelector('itemMeta links')
                        const jsonFormat = jxon.build(conceptXML)

                        if (linksXML) {
                            author.links = jxon.build(linksXML)
                        }
                        author.name = jsonFormat.name
                        author.email = this.findAttribute(jsonFormat, 'email')
                        author.isLoaded = true

                        resolve(author)
                    })
                    .catch((e) => {
                        console.error('Error fetching author', e)
                        reject(e)
                    })
            })

        },

        /**
         * @param object
         * @param attribute
         * @returns {Author|undefined}
         * @private
         */
        findAttribute(object, attribute) {
            let match

            function iterateObject(target, name) {
                Object.keys(target).forEach(function(key) {
                    if (_.isObject(target[key])) {
                        iterateObject(target[key], name)
                    } else if (key === name) {
                        match = target[key]
                    }
                })
            }

            iterateObject(object, attribute)

            return match ? match : undefined
        },

        /**
         * Takes all authors available on the node and loads the conceptItem for this authors
         * when all authors is fetched the nodes authors property is updated
         *
         * @memberOf Trait.authorNodeTrait
         * @returns {Promise.<TResult>}
         */
        fetchAuthorsConcept() {
            const authors = this.authors
            const authorsLoadPromises = this.authors.map((author) => {
                if (!author.isSimpleAuthor && author.isLoaded === false) {
                    return this.fetchAuthorConcept(author)
                } else {
                    return null
                }
            }).filter((author) => {
                return author !== null
            })

            return Promise.all(authorsLoadPromises)
                .then(() => {
                    api.editorSession.transaction((tx) => {
                        tx.set([this.id, 'authors'], authors)
                    }, {history: false})
                })
        },

        /**
         * @memberOf Trait.authorNodeTrait
         * @param {Author} author
         */
        updateAuthorFromConcept(author) {
            const authors = this.authors

            this.fetchAuthorConcept(author)
                .then((updatedAuthor) => {
                    const authorObject = authors.find((author) => {
                        if (author.uuid === updatedAuthor.uuid) {
                            return author
                        }
                        return undefined
                    })

                    if (authorObject) {
                        const index = authors.indexOf(authorObject)
                        authors[index] = updatedAuthor
                        api.editorSession.transaction((tx) => {
                            tx.set([this.id, 'authors'], authors)
                        }, {history: false})
                    }
                })
        },

        /**
         * @param {Author} author
         * @param {Author} other
         * @returns {boolean}
         * @private
         */
        _isEqual(author, other) {
            if (author === null || author === undefined) {
                return false
            }
            if (other === null || other === undefined) {
                return false
            }
            if (author.isSimpleAuthor) {
                return other.name === author.name
            } else {
                return other.uuid === author.uuid
            }
        }
    }
}

export {authorNodeTrait}
